import { useAuthStore } from './auth.store.js';
import type { GrantedPermissionDto } from './permission.types.js';

export type { GrantedPermissionDto };

/** Ruxsat tekshiruvining maqsadi — qaysi MTU / korxona / ombor haqida gap ketyapti. */
export interface PermissionTarget {
  mtuId?: string;
  orgUnitId?: string;
  warehouseId?: string;
}

/**
 * KORXONA doirasi shu OMBORNI qamraydimi?
 *
 * O'Z korxonasining ombori yuqorida, oddiy tenglik bilan hal bo'ladi.
 * Bu yerda faqat ZAXIRA YO'L: MTU ning O'ZIGA biriktirilgan markaziy
 * ombor, va u FAQAT o'z ombori bo'lmagan korxonaga ochiladi.
 *
 * Stansiyalarda ombor yo'q va bo'lmaydi ham — stansiya xodimi RJU
 * omboridan oladi. O'z ombori bor korxona esa (VCHD-8 kabi) markaziy
 * omborga qaramaydi.
 *
 * Ilgari bu yerda shart faqat MTU ga tenglik edi va u butun MTU ni
 * ochib qo'yardi: VCHD-8 xodimi qo'shni distansiyalarning omborini ham
 * ko'rar edi. Server endi boshqacha hisoblaydi (`warehouseScopeWhere`)
 * va bu ko'zgu undan ortda qolmasligi kerak — aks holda ekran bor
 * tugmani ko'rsatib, server uni rad etardi.
 */
const orgUnitCoversWarehouse = (
  grant: GrantedPermissionDto,
  target: PermissionTarget,
): boolean =>
  target.warehouseId !== undefined &&
  grant.scopeMtuId !== undefined &&
  grant.scopeMtuId !== null &&
  grant.scopeHasOwnWarehouse !== true &&
  target.orgUnitId !== undefined &&
  target.orgUnitId === grant.scopeMtuId;

/**
 * Backend `scopeCovers` mantig'ining **ko'zgusi**, faqat UI ko'rinishi uchun:
 * tugmani chizish yoki chizmaslikni hal qiladi. Haqiqiy ruxsat qarorini
 * **doim** backend chiqaradi — bu yerdagi `true` hech qachon avtorizatsiya
 * o'rniga o'tmaydi, `false` esa serverdan kelgan xatoni yashirmaydi.
 *
 * Maqsad berilmasa, doiraga bog'langan grant ishlamaydi ("fail closed"):
 * MTU-10 ga berilgan `stock.read` maqsadsiz so'ralganda `false` qaytadi,
 * chunki qaysi MTU haqida gap ketayotgani noma'lum.
 */
export const canWithPermissions = (
  isSuperadmin: boolean,
  permissions: GrantedPermissionDto[],
  permission: string,
  target: PermissionTarget = {},
): boolean => {
  if (isSuperadmin) return true;
  return permissions.some((p) => {
    if (p.permission !== permission) return false;
    switch (p.scope.type) {
      case 'GLOBAL':
        return true;
      case 'MTU':
        return target.mtuId !== undefined && target.mtuId === p.scope.id;
      case 'ORG_UNIT':
        if (target.orgUnitId !== undefined && target.orgUnitId === p.scope.id) return true;
        return orgUnitCoversWarehouse(p, target);
      case 'WAREHOUSE':
        return target.warehouseId !== undefined && target.warehouseId === p.scope.id;
    }
  });
};

/**
 * "Shu ruxsat **umuman** bormi — qaysidir MTU, korxona yoki omborda?"
 *
 * Bu `can` ning bo'sh joyini to'ldiradi, uni almashtirmaydi. `can` bitta aniq
 * obyekt ustidagi harakat uchun: maqsad noma'lum bo'lsa u "yo'q" deydi va
 * to'g'ri qiladi. Menyu esa boshqa savol beradi — hali hech qaysi ombor
 * tanlanmagan paytda ham "bu odam qoldiq bo'limiga kirishi kerakmi?" deb
 * so'raydi. Ikkalasini bir savolga aylantirsak, MTU-10 ga biriktirilgan
 * omborchi bo'sh yon panel ko'radi va ishga kirisha olmaydi.
 *
 * Ko'rinish qaroridan boshqa hech narsa emas: sahifa ochilgach har bir so'rov
 * doirasi bilan birga backendga boradi va u yerda qaytadan tekshiriladi.
 */
export const hasPermissionAnywhere = (
  isSuperadmin: boolean,
  permissions: GrantedPermissionDto[],
  permission: string,
): boolean => isSuperadmin || permissions.some((p) => p.permission === permission);

export interface PermissionApi {
  /** Aniq obyekt ustidagi harakat uchun — maqsadsiz doiraviy grant ishlamaydi. */
  can: (permission: string, target?: PermissionTarget) => boolean;
  /** Navigatsiya va bo'lim ko'rinishi uchun — doiradan qat'i nazar. */
  canAnywhere: (permission: string) => boolean;
  /**
   * Hisob superadminmi.
   *
   * Ayrim amallar RUXSAT bilan emas, hisobning turi bilan chegaralanadi —
   * masalan bo'linmani o'chirish: u qaytarilmaydi va `subdivision.manage`
   * ruxsati MTU administratorida ham bor. `can`/`canAnywhere` bunday
   * savolga javob bera olmaydi, chunki ular superadminga har doim `true`
   * qaytaradi va farqni ko'rsatmaydi.
   */
  isSuperadmin: boolean;
}

/** Komponentlar uchun qulay ko'rinish: `const { can } = usePermission()`. */
export const usePermission = (): PermissionApi => {
  const isSuperadmin = useAuthStore((s) => s.user?.isSuperadmin ?? false);
  const permissions = useAuthStore((s) => s.permissions);
  return {
    can: (permission, target) =>
      canWithPermissions(isSuperadmin, permissions, permission, target),
    canAnywhere: (permission) => hasPermissionAnywhere(isSuperadmin, permissions, permission),
    isSuperadmin,
  };
};
